import clsx from 'clsx';
import { Box } from '@mui/material';
import {
  Badge,
  badgeStyle,
  badgeType,
  iconBtnType,
  IconButton,
  Text,
  colors,
  fontWeight,
  textLevel,
} from '../../../../../../packages';
import { useSelector } from 'react-redux';
import { DomainPriorities } from '../../../../../../redux/modules/profile/types';
import { getAuth } from '../../../../../../redux/modules/auth/selectors';
import { getProfile } from '../../../../../../redux/modules/profile/selectors';
import { MedicationBottomCard } from './MedicationBottomCard';
import { ProfileInfoRole } from '../../../../../../components/v2/ProfileInfo';
import { useStyles } from './MedicationCard.styles';
import { getImportanceLevelDisplayText } from '../../../../../../utils/medicationUtils';
import dayjs from 'dayjs';

const MedicationCard = ({ medication, isOnlyView, onClick }) => {
  const classes = useStyles();
  const { isAdmin } = useSelector(getAuth);
  const { profile } = useSelector(getProfile);

  const isExternalRx =
    medication.priority?.name?.toLowerCase().includes('external') ||
    medication.taxonomyName?.toLowerCase() === 'other';

  const onClickTitle = () => {
    // Don't allow clicking for external RX
    if (!isExternalRx) {
      onClick && onClick(medication, false);
    }
  };

  const onClickEdit = () => {
    onClick && onClick(medication, true);
  };

  return (
    <Box className={classes.card}>
      <Box className={classes.rowCenter} sx={{ p: 3 }}>
        <Box>
          <Box className={classes.topLeftHead}>
            <Text
              className={classes.title}
              onClick={onClickTitle}
              sx={{ cursor: isExternalRx ? 'default' : 'pointer' }}
            >
              {isExternalRx
                ? medication.note || medication.tagMetaData?.rxDrugInfo?.additionalNotes || medication.name
                : medication.name}
            </Text>
            <Badge
              variant={badgeType.FILLED}
              className={clsx(classes.badge, {
                [classes.badgeMisreported]: medication.priority.name === DomainPriorities.MISREPORTED,
              })}
              style={
                (medication.priority.name?.toUpperCase() === 'TO_BE_PRESCRIBED' ||
                medication.priority.name?.toUpperCase() === 'E_PRESCRIPTION_FAILED'
                  ? badgeStyle.CRITICAL
                  : badgeStyle[medication.priority.name]) || badgeStyle.UNRELATED
              }
            >
              {getImportanceLevelDisplayText(medication.priority.name)}
            </Badge>
          </Box>
          {!isExternalRx && (
            <>
              <Text className={classes.subtitle}>
                Drug: {medication.tagMetaData?.rxDrugInfo?.externalMedicationName || 'Not specified'}
              </Text>
              <Box>
                <Text className={classes.subtitle}>
                  Sig: {medication.tagMetaData?.rxDrugInfo?.sig || 'Not specified'}
                </Text>
              </Box>
              <Box>
                <Text className={classes.subtitle}>
                  Dispense Amount: {medication.tagMetaData?.rxDrugInfo?.dispenseAmount}{' '}
                  {medication.tagMetaData?.rxDrugInfo?.dose}
                  {Number(medication.tagMetaData?.rxDrugInfo?.dispenseAmount) > 1 ? 's' : ''}
                </Text>
              </Box>
              <Box>
                <Text className={classes.subtitle}>
                  Pharmacy Notes: {medication.tagMetaData?.rxDrugInfo?.pharmacyNotes || 'Not specified'}
                </Text>
              </Box>
              <Box>
                <Text className={classes.subtitle}>
                  Internal Notes: {medication?.note || 'Not specified'}
                </Text>
              </Box>
            </>
          )}
          {isExternalRx && (
            <Box>
              <Text className={classes.subtitle}>
                Sig: {medication.tagMetaData?.rxDrugInfo?.sig || 'Not specified'}
              </Text>
            </Box>
          )}

          {/* Discontinued meds data */}
          {medication.priority?.name?.toUpperCase() === 'DISCONTINUED' && (
            <Box mt={2} display="flex" flexDirection="column">
              <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.destructive600}>
                Reason for discontinuation:{' '}
                <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                  {medication?.tagMetaData?.rxDrugInfo?.discontinuedReason || 'Not specified'}
                </Text>
              </Text>
              <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.destructive600}>
                Discontinued on:{' '}
                <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                  {dayjs(medication?.tagMetaData?.rxDrugInfo?.discontinuedAt).format('MM/DD/YY') ||
                    'Not specified'}
                </Text>
              </Text>
            </Box>
          )}
        </Box>
        <Box className={classes.topRight}>
          {!isOnlyView &&
            (profile?.designation === ProfileInfoRole.NURSE_PRACTITIONER ||
              profile?.designation === ProfileInfoRole.PHYSICIAN ||
              isAdmin) && (
              <IconButton variant={iconBtnType.SECONDARY} className={classes.btn} onClick={onClickEdit}>
                Update
              </IconButton>
            )}
        </Box>
      </Box>

      {/* External RX Prescriber Info */}
      {isExternalRx && (
        <Box sx={{ px: 3, pb: 3, pt: 2, borderTop: `1px solid ${colors.neutral100}` }}>
          <Box display="flex" flexDirection="column" gap="4px">
            <Text level={textLevel.S} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
              Prescribed by External Prescriber
            </Text>
            <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral600}>
              {medication?.tagMetaData?.rxDrugInfo?.providerName || 'Unknown'}
            </Text>
          </Box>
        </Box>
      )}

      {/* Regular Medication Bottom Card */}
      {!isExternalRx && <MedicationBottomCard medication={medication} />}
    </Box>
  );
};

export { MedicationCard };
